import { useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from './Button';
import { Modal } from './Modal';
import { parseProductHtml, type ExtractedProduct } from '../../services/firebase';
import type { GenericExtractionMethod } from '../../types';

export interface PasteHtmlResult {
  url: string;
  extracted: ExtractedProduct;
}

export interface PasteHtmlModalProps {
  /**
   * URLs that need manual HTML paste, in order. The modal walks the queue one
   * URL at a time. Render the modal only when this is non-empty.
   */
  queue: string[];
  /** Extraction strategy to run server-side on the pasted HTML. */
  method: GenericExtractionMethod;
  /**
   * The source's product-page `image` CSS override, applied to the pasted HTML
   * too — a page reached by hand should pick the same image as one fetched
   * automatically.
   */
  imageSelector?: string;
  /**
   * Fired after each successful parse, in queue order. Lets a caller stream
   * results into its own UI (e.g. update a preview row) as the user works
   * through the queue.
   */
  onResult?: (result: PasteHtmlResult) => void;
  /**
   * Fired once the queue is fully processed (every URL parsed or skipped).
   * Receives all successful results accumulated across the queue.
   */
  onDone: (results: PasteHtmlResult[]) => void;
  /** Fired when the user dismisses the modal entirely (X / backdrop / Escape). */
  onCancel: () => void;
}

/**
 * Manual HTML paste fallback for sites that block automated extraction (all
 * three fetch strategies in extractProductData fail → NeedsManualHtmlError).
 * The user opens each page, copies the source, and pastes it here;
 * parseProductHtml then extracts server-side. Shared by the Bulk Import wizard
 * and the Import Sources "Test" preview so both paths behave identically.
 */
export function PasteHtmlModal({
  queue,
  method,
  imageSelector,
  onResult,
  onDone,
  onCancel,
}: PasteHtmlModalProps) {
  const [index, setIndex] = useState(0);
  const [html, setHtml] = useState('');
  const [parsing, setParsing] = useState(false);
  const [results, setResults] = useState<PasteHtmlResult[]>([]);

  const currentUrl = queue[index];
  if (!currentUrl) return null;

  const isLast = index >= queue.length - 1;

  // Advance to the next URL, or finish the queue (deliver accumulated results).
  const advance = (nextResults: PasteHtmlResult[]) => {
    if (isLast) {
      onDone(nextResults);
    } else {
      setIndex((i) => i + 1);
      setHtml('');
    }
  };

  const handleSubmit = async () => {
    if (!currentUrl || !html.trim()) return;
    setParsing(true);
    try {
      // Always skip the cache READ. Pasting is an explicit "parse THIS html"
      // action — the user supplied the source of truth. If we let the server
      // consult the cache first, a stale/failed prior extraction for this URL
      // (e.g. an Akamai block page that parsed to a few og-tag fields) would be
      // returned and the pasted HTML silently dropped ("paste doesn't stick").
      // The server's cache WRITE is unconditional, so parsing here overwrites
      // that stale entry under the same (url, method) key. Import Now then runs
      // with skip-cache OFF (the default) and hits this freshly-pasted result —
      // no toggle dance required.
      const extracted = await parseProductHtml(html, currentUrl, true, method, imageSelector);
      const result: PasteHtmlResult = { url: currentUrl, extracted };
      const nextResults = [...results, result];
      setResults(nextResults);
      onResult?.(result);
      toast.success(`Parsed ${extracted.fieldsFound} fields`);
      advance(nextResults);
    } catch (err) {
      toast.error(`Parse failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setParsing(false);
    }
  };

  // Secondary button: skip this URL (multi-URL queue) / cancel (single URL).
  const handleSkip = () => {
    if (isLast) {
      // Deliver whatever we have so far, then let the parent close us.
      onDone(results);
    } else {
      setIndex((i) => i + 1);
      setHtml('');
    }
  };

  return (
    <Modal
      isOpen
      onClose={onCancel}
      title={
        queue.length > 1
          ? `Paste Page Source (${index + 1}/${queue.length})`
          : 'Paste Page Source'
      }
    >
      <div className="space-y-3">
        <div className="flex items-start gap-2">
          <div className="flex-1 space-y-2">
            <p className="text-sm text-gray-600">
              We couldn't fetch this page automatically. Some brands (Omega, Rolex, Patek, …) block
              automated scrapers outright, so the page has to be copied by hand.
            </p>
            <ol className="text-sm text-gray-600 list-decimal pl-5 space-y-1">
              <li>
                Open the URL below in your browser (<span className="font-medium">Open page ↗</span>)
                and let it finish loading.
              </li>
              <li>
                Press{' '}
                <kbd className="px-1 py-0.5 bg-gray-100 rounded text-xs font-mono">Cmd+Option+U</kbd>{' '}
                to view source, then{' '}
                <kbd className="px-1 py-0.5 bg-gray-100 rounded text-xs font-mono">Cmd+A</kbd>{' '}
                <kbd className="px-1 py-0.5 bg-gray-100 rounded text-xs font-mono">Cmd+C</kbd> to copy it.
              </li>
              <li>
                If the source looks empty or the page is JavaScript-rendered, use DevTools instead:{' '}
                <kbd className="px-1 py-0.5 bg-gray-100 rounded text-xs font-mono">Cmd+Option+I</kbd>{' '}
                → <span className="font-medium">Elements</span> tab → right-click the{' '}
                <code className="px-1 py-0.5 bg-gray-100 rounded text-xs font-mono">&lt;html&gt;</code>{' '}
                element → <span className="font-medium">Copy → Copy outerHTML</span>.
              </li>
              <li>Paste into the box below and hit Parse.</li>
            </ol>
          </div>
          <button
            type="button"
            className="shrink-0 text-xs bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 py-1.5 rounded-lg font-medium"
            onClick={() => window.open(currentUrl, '_blank')}
          >
            Open page ↗
          </button>
        </div>
        <div className="bg-gray-50 rounded px-2 py-1">
          <span className="font-mono text-xs text-gray-500 break-all">{currentUrl}</span>
        </div>
        <textarea
          className="w-full h-64 border rounded-lg p-2 text-xs font-mono"
          placeholder="Paste HTML source here..."
          value={html}
          onChange={(e) => setHtml(e.target.value)}
          autoFocus
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">
            {html.length > 0 ? `${html.length.toLocaleString()} chars` : ''}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5"
              onClick={handleSkip}
            >
              {queue.length > 1 ? 'Skip' : 'Cancel'}
            </button>
            <Button onClick={handleSubmit} disabled={!html.trim() || parsing}>
              {parsing ? 'Parsing...' : isLast ? 'Parse HTML' : 'Parse & Next'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
